import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../lib/api";
import { getNotifLink } from "../lib/notifLink";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Bell, Check, CheckCircle, ListChecks, ClipboardText, Warning, Gear } from "@phosphor-icons/react";
import { ClientNotificationSettings } from "../components/portal/ClientNotificationSettings";
import { toast } from "sonner";

const ICON_MAP = {
  task_assigned: ListChecks, task_completed: CheckCircle, task_approved: CheckCircle,
  task_rejected: Warning, work_order_issued: ClipboardText, emergency_sla: Warning, default: Bell
};

const PREF_ITEMS = [
  { key: "task_assigned", label: "Task Assigned", desc: "When a task is assigned to you or your team" },
  { key: "task_completed", label: "Task Completed", desc: "When a task is marked as complete" },
  { key: "task_approved", label: "Task Approved", desc: "When a completed task is approved" },
  { key: "task_rejected", label: "Task Rejected", desc: "When a task is rejected and needs rework" },
  { key: "work_order_issued", label: "Works Order Issued", desc: "When a new works order is created" },
  { key: "compliance_reminder", label: "Compliance Reminders", desc: "Alerts for expiring compliance documents" },
];

export default function NotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  // Notification preferences
  const [prefs, setPrefs] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await API.get("/notifications");
      setNotifications(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await API.get("/notification-preferences");
      setPrefs(data);
    } catch (err) { console.error(err); }
    finally { setPrefsLoading(false); }
  }, []);

  useEffect(() => { fetchNotifications(); if (user?.role !== "client") fetchPrefs(); }, [fetchNotifications, fetchPrefs, user?.role]);
  const isClient = user?.role === "client";

  const claimFromAlert = async (n) => {
    try {
      const { data } = await API.post(`/sla/job-requests/${n.entity_id}/action`, {});
      toast.success(data?.message === "Already actioned" ? `Already claimed by ${data.actioned_by_name}` : "Claimed — it's yours");
    } catch {
      toast.error("Could not claim");
    }
    try { await API.put(`/notifications/${n.id}/read`); } catch {}
    fetchNotifications();
  };

  const markAllRead = async () => {
    await API.put("/notifications/read-all");
    fetchNotifications();
  };

  const handleTogglePref = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      await API.put("/notification-preferences", prefs);
      toast.success("Notification preferences saved");
    } catch { toast.error("Failed to save preferences"); }
    finally { setSavingPrefs(false); }
  };

  const filtered = filter === "all" ? notifications : notifications.filter(n => !n.read);
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-1">{unreadCount} unread</p>
        </div>
      </div>

      <Tabs defaultValue="inbox">
        <TabsList data-testid="notifications-tabs">
          <TabsTrigger value="inbox" data-testid="notifications-tab-inbox">
            Inbox
            {unreadCount > 0 && <Badge className="ml-1.5 h-4 min-w-4 text-[9px] px-1">{unreadCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="preferences" data-testid="notifications-tab-preferences">
            <Gear size={14} className="mr-1" /> Preferences
          </TabsTrigger>
        </TabsList>

        {/* Inbox Tab */}
        <TabsContent value="inbox" className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex border border-border rounded-md overflow-hidden">
              <button onClick={() => setFilter("all")} className={`px-3 py-1.5 text-xs ${filter==="all" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="filter-all">All</button>
              <button onClick={() => setFilter("unread")} className={`px-3 py-1.5 text-xs ${filter==="unread" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="filter-unread">Unread</button>
            </div>
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={markAllRead} data-testid="mark-all-read-btn">
                <Check size={14} className="mr-1" /> Mark All Read
              </Button>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md">
              <Bell size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">No notifications</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map((n, i) => {
                const Icon = ICON_MAP[n.type] || ICON_MAP.default;
                return (
                  <div
                    key={n.id || n._id || i}
                    className={`flex items-start gap-3 p-4 rounded-md border transition-colors cursor-pointer ${
                      n.read ? "border-transparent hover:bg-muted/30" : "border-primary/20 bg-primary/5 hover:bg-primary/10"
                    }`}
                    onClick={async () => {
                      if (!n.read) { try { await API.put(`/notifications/${n.id}/read`); } catch {} }
                      const link = getNotifLink(n, user);
                      if (link) navigate(link); else fetchNotifications();
                    }}
                    data-testid={`notification-${n.id}`}
                  >
                    <Icon size={20} className={n.read ? "text-muted-foreground" : "text-primary"} weight="duotone" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-sm font-medium ${n.read ? "text-muted-foreground" : ""}`}>{n.title}</span>
                        {!n.read && <Badge className="h-4 min-w-4 text-[8px] px-1">NEW</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</p>
                        {n.type === "unclaimed_request" && n.entity_id && (
                          <Button size="sm" className="h-6 text-[11px] bg-red-600 hover:bg-red-700 text-white"
                                  onClick={(e) => { e.stopPropagation(); claimFromAlert(n); }}
                                  data-testid={`notif-page-claim-${n.id}`}>
                            Claim
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences" className="mt-4" data-testid="notification-preferences-tab">
          {isClient ? (
            <div className="max-w-3xl" data-testid="client-notification-preferences">
              <div className="mb-4">
                <h2 className="text-base font-semibold font-[Chivo]">Notification Preferences</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Choose what you're told about — in the portal and by email.</p>
              </div>
              <ClientNotificationSettings />
            </div>
          ) : prefsLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : prefs ? (
            <div className="space-y-6 max-w-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold font-[Chivo]">Notification Preferences</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Choose which notifications you want to receive.</p>
                </div>
                <Button size="sm" onClick={handleSavePrefs} disabled={savingPrefs} data-testid="save-prefs-btn">
                  {savingPrefs ? "Saving..." : "Save Preferences"}
                </Button>
              </div>

              {/* Email Master Toggle */}
              <div className="border border-border rounded-md p-4" data-testid="email-toggle-section">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-semibold">Email Notifications</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Receive email notifications in addition to in-app alerts</p>
                  </div>
                  <Switch
                    checked={prefs.email_enabled ?? true}
                    onCheckedChange={() => handleTogglePref("email_enabled")}
                    data-testid="toggle-email-enabled"
                  />
                </div>
              </div>

              {/* Individual Notification Types */}
              <div className="border border-border rounded-md divide-y divide-border" data-testid="notification-types-section">
                {PREF_ITEMS.map(item => (
                  <div key={item.key} className="flex items-center justify-between p-4" data-testid={`pref-row-${item.key}`}>
                    <div>
                      <Label className="text-sm font-medium">{item.label}</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                    <Switch
                      checked={prefs[item.key] ?? true}
                      onCheckedChange={() => handleTogglePref(item.key)}
                      data-testid={`toggle-${item.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Unable to load preferences.</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
